<template>
	<div class="overlay-dialog">
		<h2
			class="overlay-dialog__title"
			v-html="$t('account.addressEdit.title')"
		/>
		<GeneralMessage
			ref="message"
			v-if="response.message"
			:message="response.message"
			:type="response.type"
			@close="response.message = ''"
		/>
		<ValidationObserver
			ref="form"
			v-slot="{ invalid, errors }"
			tag="form"
			@submit.prevent="submit"
			novalidate
		>
			<ValidationObserver
				tag="section"
				class="account__address-form"
				ref="userAddress"
				vid="userAddress"
				v-slot="{ invalid }"
			>
				<ValidationProvider
					name="shippingFirstname"
					rules="required"
					v-slot="{ errors, invalid, validated }"
					tag="div"
					:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
				>
					<FormInput
						type="text"
						:placeholder="$t('forms.labels.firstname')"
						:label="$t('forms.labels.firstname')"
						name="firstname"
						v-model="form.userAddress.firstName"
						:error="errors.length > 0"
						required
						@input="onInput"
						:disabled="loading"
					/>
					<span
						class="input-error"
						v-if="invalid && validated"
						v-html="errors[0]"
					/>
				</ValidationProvider>
				<ValidationProvider
					name="shippingLastname"
					rules="required"
					v-slot="{ errors, invalid, validated }"
					tag="div"
					:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
				>
					<FormInput
						type="text"
						:placeholder="$t('forms.labels.lastname')"
						:label="$t('forms.labels.lastname')"
						name="lastname"
						v-model="form.userAddress.lastName"
						:error="errors.length > 0"
						required
						@input="onInput"
						:disabled="loading"
					/>
					<span
						class="input-error"
						v-if="invalid && validated"
						v-html="errors[0]"
					/>
				</ValidationProvider>
				<FormInput
					type="text"
					:placeholder="$t('forms.labels.company')"
					:label="$t('forms.labels.company')"
					:labelOption="$t('forms.labels.optional')"
					name="company"
					v-model="form.userAddress.company"
					@input="onInput"
					:disabled="loading"
				/>

				<div class="input-group">
					<div class="input-group__span-9">
						<FormInput
							type="text"
							:placeholder="$t('forms.labels.street')"
							:label="$t('forms.labels.street')"
							:labelOption="$t('forms.labels.optional')"
							name="street"
							v-model="form.userAddress.street"
							@input="onInput"
							:disabled="loading"
						/>
					</div>
					<div class="input-group__span-3">
						<FormInput
							type="text"
							:placeholder="$t('forms.labels.number')"
							:label="$t('forms.labels.number')"
							:labelOption="$t('forms.labels.optional')"
							name="number"
							v-model="form.userAddress.streetNumber"
							@input="onInput"
							:disabled="loading"
						/>
					</div>
				</div>

				<ValidationObserver
					class="input-row__wrapper"
					tag="div"
					ref="zipCity"
					v-slot="{ dirty, validated, invalid, errors }"
				>
					<div class="input-group">
						<ValidationProvider
							class="input-group__span-4 input-group__span-xxl-3"
							name="shippingZip"
							rules="digits:4"
							v-slot="{ errors, invalid }"
							tag="div"
							:customMessages="{ digits: $t('forms.errors.zip') }"
						>
							<FormInput
								ref="userZipInput"
								type="text"
								v-model="form.userAddress.postcode"
								@keydown="onZipKeydown"
								@input="onZipInput"
								:placeholder="$t('forms.labels.zip')"
								:label="$t('forms.labels.zip')"
								:labelOption="$t('forms.labels.optional')"
								name="zip"
								:minlength="4"
								:maxlength="4"
								inputmode="decimal"
								pattern="[0-9]{4}"
								:error="errors.length > 0"
								:disabled="loading"
							/>
						</ValidationProvider>
						<div class="input-group__span-8 input-group__span-xxl-9">
							<FormInput
								type="text"
								:placeholder="$t('forms.labels.city')"
								:label="$t('forms.labels.city')"
								:labelOption="$t('forms.labels.optional')"
								name="city"
								v-model="form.userAddress.city"
								@input="onInput"
								:disabled="loading"
							/>
						</div>
					</div>
					<div
						class="input-error input-group__span-12"
						v-if="invalid && (validated || dirty)"
						v-html="Object.values(errors).filter(value => !!value.join('').trim()).join(' ')"
					/>
				</ValidationObserver>

				<FormInput
					type="text"
					:placeholder="$t('forms.labels.addressDetails')"
					:label="$t('forms.labels.addressDetails')"
					:labelOption="$t('forms.labels.optional')"
					name="addressdetails"
					v-model="form.userAddress.details"
					:disabled="loading"
					@input="onInput"
				/>
				<FormInputSelect
					:options="countryOptions"
					v-model="form.userAddress.countryCode"
					:label="$t('forms.labels.country')"
					name="country"
					@input="onInput"
					:disabled="loading"
				/>
				<ValidationProvider
					name="shippingPhone"
					rules="required|phone"
					v-slot="{ errors, invalid, validated }"
					tag="div"
					:customMessages="{ required: $t('forms.errors.fieldEmpty'), phone: $t('forms.errors.phone') }"
				>
					<FormInput
						type="tel"
						:placeholder="$t('forms.labels.phone')"
						:label="$t('forms.labels.phone')"
						name="phone"
						v-model="form.userAddress.phoneNumber"
						:error="errors.length > 0"
						required
						@input="onInput"
						:disabled="loading"
					/>
					<span
						class="input-error"
						v-if="invalid && validated"
						v-html="errors[0]"
					/>
				</ValidationProvider>
			</ValidationObserver>

			<Button
				type="submit"
				level="primary"
				:text="$t('account.addressEdit.save')"
				:disabled="loading"
			/>
		</ValidationObserver>
	</div>
</template>

<script>
import Vue from "vue"
import { mapGetters } from "vuex"
import { ValidationProvider, ValidationObserver } from "vee-validate"
import FormInput from "@/components/FormInput"
import FormInputTextarea from "@/components/FormInputTextarea"
import FormInputCheckbox from "@/components/FormInputCheckbox"
import FormInputSelect from "@/components/FormInputSelectCustom"
import GeneralMessage from '@/components/GeneralMessage'
import Button from '@/components/Button'
import scrrroll from 'scrrroll'
import GenericMixin from '@/mixins/GenericVueMixin'

export default {
  mixins: [
    GenericMixin,
  ],
	components: {
		ValidationProvider,
		ValidationObserver,
		FormInput,
		FormInputTextarea,
		FormInputCheckbox,
		FormInputSelect,
		GeneralMessage,
		Button,
	},
	data() {
		return {
			countryOptions: [
				{
					value: 'CH',
					label: this.$t('forms.countries.ch')
				}
			],
			form: {
				userAddress: {
					firstName: "",
					lastName: "",
					countryCode: "CH",
					street: "",
					streetNumber: "",
					city: "",
					postcode: "",
					details: '',
					provinceName: "",
					company: "",
					phoneNumber: "",
				},
			},
			response: {
				message: '',
				type: '',
			},
			edited: false,
		};
	},
	computed: {
		...mapGetters("account", [
			"isLoggedIn",
			"accountLastAddress",
			"accountUserDetail",
		]),
		...mapGetters('loader', [
			'isLoading',
		]),
		loading: {
			set(value) {
				this.$store.dispatch('loader/setLoading', value);
				this.$emit('loading', value);
			},
			get() {
				return this.isLoading;
			}
		},
	},
	methods: {
		onZipKeydown(e) {
			if (!e.metaKey && !e.ctrlKey && e.key && e.key.length <= 1 && !/[0-9]/.test(e.key)) {
				e.preventDefault()
			}
		},
		onZipInput(value) {
			if (/[^\d]/.test(value)) {
				Vue.set(this.form.userAddress, 'postcode', value.replace(/[^\d]/ig,''));
				this.$refs.userZipInput.$refs.input.value = this.form.userAddress.postcode;
			}
			this.onInput();
		},
		onInput() {
			this.edited = true;
		},
		async submit() {
			this.loading = true
			const isValid = await this.$refs.form.validate()
			if (isValid) {
				this.response.message = '';
				try {
					await this.$store.dispatch('account/updateAddress', {...this.form});
					this.edited = false;
					this.response.message = this.$t('account.addressEdit.success');
					this.response.type = 'success';
				}
				catch(err) {


					let notificationMessage = err.response.data;
					if (typeof notificationMessage === "object") {
						let errors = notificationMessage.errors
						if (errors) {
							//handle errors
							notificationMessage = Object.values(errors)
								.map(v => v[0])
								.join(", ")
						} else {
							notificationMessage = notificationMessage.message
						}
					}
					this.response.message = notificationMessage || this.$t('forms.errors.generic');
					this.response.type = 'error';

				}
				this.$parent.scrollTop();
			}
			this.loading = false
		},
		async onClose() {
			if (this.edited) {
				const confirmed = await this.$store.dispatch('dialog/showDialog', {
					title: this.$t('account.addressEdit.dialogTitle'),
					message: this.$t('account.addressEdit.dialogMessage'),
					confirm: this.$t('account.addressEdit.dialogConfirm'),
					cancel: this.$t('account.addressEdit.dialogCancel'),
				});
				if (confirmed) {
					return;
				}
			}
			this.$emit('close');
		}
	},
	created() {
		if (this.accountLastAddress) {
			Object.keys(this.form.userAddress).forEach(key => {
				if (this.accountLastAddress[key] !== undefined) {
					Vue.set(this.form.userAddress, key, ''+this.accountLastAddress[key]);
				}
			});
		}
	}
}
</script>

<style>

</style>
